<script setup lang="ts">
import type { ReadonlyDeep } from 'type-fest';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed } from 'vue';
import { formatInTimeZone } from 'date-fns-tz';

import LegChooseNewAirRoadStepAdditionalInfoDescList from '@/components/descLists/LegChooseNewAirRoadStepAdditionalInfoDescList.vue';
import WideJobStopEmptyDiagram from '@/components/diagrams/WideJobStopEmptyDiagram.vue';
import DetailedJobInfoDescListItem from '@/components/items/DetailedJobInfoDescListItem.vue';
import NewAirRoadStopAddressText from '@/components/texts/NewAirRoadStopAddressText.vue';
import KParentTooltip from '@/components/tooltips/KParentTooltip.vue';
import { useStaticStore } from '@/stores/useStaticStore';
import * as commonConstants from '@/constants/common';

export interface Props {
  job?: ReadonlyDeep<
    Partial<
      Pick<
        openapi.AirroadJobDetailedContentOpenapi,
        | 'customer'
        | 'status'
        | 'booked_by'
        | 'booked_at'
        | 'reference_1'
        | 'reference_2'
        | 'reference_3'
        | 'stops'
      >
    >
  >;
  arStops?: Partial<openapi.AirroadSingleCreateBodyOpenapi['ar_stops'][0]>[];
  isFold?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  job: undefined,
  arStops: () => [],
  isFold: false,
});

const staticStore = useStaticStore();

function getDriverName(driverCode: number): string | undefined {
  const driver = staticStore.getDrivers.find(
    (driver) => driver.code === driverCode,
  );
  // TODO
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (driver?.full_name) return driver?.full_name;
  return '';
}

const timeZone = 'Australia/Sydney';

function formatDate(date: string, timeZone: string): string {
  return formatInTimeZone(date, timeZone, 'hh:mm a, MMM d'); // 04:50 PM, Sep 24
}

const stops = computed(function () {
  return props.job?.stops || [];
});
</script>

<template>
  <section
    :class="$style.previewDiagramNewAirRoadSection"
    data-testid="previewDiagramNewAirRoadSection"
  >
    <LegChooseNewAirRoadStepAdditionalInfoDescList
      :job="props.job"
      class="mb-4"
      :without-references="props.isFold"
    />
    <!-- TODO similar to @/components/tableFolds/BookedAirRoadTableFold.vue -->
    <WideJobStopEmptyDiagram
      :stop-count="stops.length"
      :class="$style.diagram"
    />
    <div :class="$style.stopAddresses">
      <div
        v-for="(stop, stopIndex) in stops"
        :key="stopIndex"
        :class="$style.stopAddress"
      >
        <template
          v-if="
            arStops[stopIndex] &&
            arStops[stopIndex].name &&
            arStops[stopIndex].state
          "
        >
          <h4 class="my-0">
            {{ arStops[stopIndex].name }},
            {{ arStops[stopIndex].state }}
          </h4>
          <NewAirRoadStopAddressText
            :class="$style.addressText"
            :stops="stops"
            :ar-stops="arStops"
            :stop-index="stopIndex"
          />
        </template>
        <div class="mt-3 text-sm">
          <div v-if="stop.note_text">{{ stop.note_text }}</div>
          <div v-if="stop.note_name">{{ stop.note_name }}</div>
          <div v-if="stop.note_phone && stop.note_phone.length > 0">
            <a
              :href="`callto://${stop.note_phone}`"
              class="text-decoration-none"
            >
              <KParentTooltip>Call via Skype</KParentTooltip>
              <v-icon icon="mdi-phone-outline" size="15"></v-icon>
              {{ stop.note_phone }}
            </a>
          </div>
        </div>
      </div>
    </div>
    <div v-if="props.isFold" :class="$style.foldData">
      <div
        v-for="(stop, stopIndex) in stops"
        :key="stopIndex"
        :class="$style.foldDataItem"
      >
        <!-- TODO similar to @/components/descLists/JobDiagramDescList.vue -->
        <dl :class="$style.descList">
          <div :class="$style.itemWrapper">
            <DetailedJobInfoDescListItem
              :class="$style.item"
              term="Time required:"
            >
              <template v-if="stop.required_at">
                <span>
                  <KParentTooltip>{{ stop.required_at }}</KParentTooltip>
                  {{ formatDate(stop.required_at, timeZone) }}
                </span>
              </template>
              <template v-else>{{ commonConstants.EM_DASH }}</template>
            </DetailedJobInfoDescListItem>
          </div>
          <div :class="$style.itemWrapper">
            <DetailedJobInfoDescListItem :class="$style.item" term="Driver">
              <template v-if="stop.driver_is != null">
                {{
                  getDriverName(stop.driver_is) + ' (' + stop.driver_is + ')'
                }}
              </template>
              <template v-else>{{ commonConstants.EM_DASH }}</template>
            </DetailedJobInfoDescListItem>
          </div>
          <div :class="$style.itemWrapper">
            <DetailedJobInfoDescListItem
              :class="$style.item"
              term="Arrived at:"
            >
              <template v-if="stop.arrived_at">
                <span>
                  <KParentTooltip>{{ stop.arrived_at }}</KParentTooltip>
                  {{ formatDate(stop.arrived_at, timeZone) }}
                </span>
              </template>
              <template v-else>{{ commonConstants.EM_DASH }}</template>
            </DetailedJobInfoDescListItem>
          </div>
          <div :class="$style.itemWrapper">
            <DetailedJobInfoDescListItem
              :class="$style.item"
              term="Departed at:"
            >
              <template v-if="stop.departed_at">
                <span>
                  <KParentTooltip>{{ stop.departed_at }}</KParentTooltip>
                  {{ formatDate(stop.departed_at, timeZone) }}
                </span>
              </template>
              <template v-else>{{ commonConstants.EM_DASH }}</template>
            </DetailedJobInfoDescListItem>
          </div>
          <div :class="$style.itemWrapper">
            <DetailedJobInfoDescListItem :class="$style.item" term="Attributes">
              <template v-if="stop.attributes?.length">
                {{ stop.attributes.join(', ') }}
              </template>
              <template v-else>{{ commonConstants.EM_DASH }}</template>
            </DetailedJobInfoDescListItem>
          </div>
          <div :class="$style.itemWrapper">
            <DetailedJobInfoDescListItem :class="$style.item" term="Services">
              <template v-if="stop.services?.length">
                {{ stop.services.join(', ') }}
              </template>
              <template v-else>{{ commonConstants.EM_DASH }}</template>
            </DetailedJobInfoDescListItem>
          </div>
        </dl>
      </div>
    </div>
  </section>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.previewDiagramNewAirRoadSection {
}
  .diagram {
    margin: 20px 0 12px 0;
  }
  .stopAddresses {
    display: flex;
  }
    .stopAddress {
      width: 100%;
      padding-right: 12px;
    }
  .foldData {
    display: flex;
    margin-top: 10px;
  }
    .foldDataItem {
      width: 100%;
      padding-right: 12px;
    }
      .descList {
        margin: 10px 0 0 0;
        font-size: 14px;
      }
        .itemWrapper {
        }
          .item {
            display: inline-block;
            margin: 0 0 10px 0;
          }
</style>
